import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Tag as TagIcon, Plus, Trash2, ExternalLink, MessageSquare, Calendar, CheckSquare, StickyNote, Clock, GitBranch, CreditCard, User as UserIcon,
  Zap, AlertCircle, CheckCircle2, Hourglass, ShieldAlert, ShieldCheck, Mail, Phone,
} from "lucide-react";
import { Badge, Button, Input, Label, Modal, ModalFooter, Select, Textarea, useToast } from "@/components/ui";
import { TaskDialog } from "@/features/tasks/TaskDialog";
import { PanelErrorRow } from "./PanelErrorRow";
import { usePanelData } from "./usePanelData";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDateTime, formatMoney, formatRelative } from "@/lib/format";
import type {
  Activity, Appointment, Contact, ContactAutomations, ContactConsentRow, Invoice, Note, Opportunity, Payment, Product, Tag, Task,
} from "@/lib/types";

/**
 * Self-contained right-rail panels shared by the contact-detail page and
 * the standalone inbox. Each panel takes a `contactId` and fetches its
 * own data; that's slower than the contact-detail page's old
 * Promise.all-everything pattern but eliminates the parent prop-drilling
 * mess and lets the inbox embed any subset of these panels without
 * reimplementing them.
 *
 * Panels that need bidirectional state (tags, notes, tasks) take a
 * minimal `onChange`-style callback so the parent can react to mutations
 * (e.g. the contact-detail header reflects updated tag membership).
 */

// ─── Timeline ─────────────────────────────────────────────────────────────
const ACTIVITY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "contact.created": Plus,
  "contact.updated": UserIcon,
  "contact.tag_added": TagIcon,
  "message.sent": MessageSquare,
  "message.received": MessageSquare,
  "appointment.booked": Calendar,
  "appointment.completed": Calendar,
  "appointment.cancelled": Calendar,
  "task.created": CheckSquare,
  "task.completed": CheckSquare,
  "note.created": StickyNote,
  "form.submitted": ExternalLink,
  "opportunity.created": GitBranch,
  "opportunity.stage_changed": GitBranch,
  "opportunity.won": GitBranch,
  "opportunity.lost": GitBranch,
  "automation.started": Zap,
  "automation.finished": CheckCircle2,
  "automation.failed": AlertCircle,
};

/**
 * Tone per activity type — drives both the icon color and the bubble
 * background tint. Positive outcomes = emerald, negative = rose, info /
 * neutral activity = indigo or zinc. Anything not explicitly mapped
 * falls back to zinc.
 */
const ACTIVITY_TONES: Record<string, { icon: string; bg: string }> = {
  "contact.created":          { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "contact.tag_added":        { icon: "text-rose-300",   bg: "bg-rose-500/10" },
  "message.sent":             { icon: "text-rose-300",   bg: "bg-rose-500/10" },
  "message.received":         { icon: "text-sky-300",      bg: "bg-sky-500/10" },
  "appointment.booked":       { icon: "text-rose-300",   bg: "bg-rose-500/10" },
  "appointment.completed":    { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "appointment.cancelled":    { icon: "text-rose-300",     bg: "bg-rose-500/10" },
  "task.created":             { icon: "text-amber-300",    bg: "bg-amber-500/10" },
  "task.completed":           { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "note.created":             { icon: "text-amber-300",    bg: "bg-amber-500/10" },
  "form.submitted":           { icon: "text-sky-300",      bg: "bg-sky-500/10" },
  "opportunity.created":      { icon: "text-rose-300",   bg: "bg-rose-500/10" },
  "opportunity.stage_changed":{ icon: "text-rose-300",   bg: "bg-rose-500/10" },
  "opportunity.won":          { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "opportunity.lost":         { icon: "text-rose-300",     bg: "bg-rose-500/10" },
  "automation.started":       { icon: "text-rose-300",   bg: "bg-rose-500/10" },
  "automation.finished":      { icon: "text-emerald-300",  bg: "bg-emerald-500/10" },
  "automation.failed":        { icon: "text-rose-300",     bg: "bg-rose-500/10" },
};
const DEFAULT_TONE = { icon: "text-zinc-400", bg: "bg-zinc-800" };

export function TimelinePanel({ contactId, max = 25 }: { contactId: string; max?: number }) {
  const { data: activities, error, reload } = usePanelData(
    () => api.get<{ activities: Activity[] }>("/activities", { contact_id: contactId, limit: max }).then((d) => d.activities),
    [contactId, max]
  );

  return (
    <Card
      icon={Clock}
      title="Timeline"
      count={activities?.length}
    >
      <div className="max-h-72 overflow-y-auto p-3">
        {error ? (
          <PanelErrorRow message={error} onRetry={reload} />
        ) : activities === null ? (
          <p className="text-xs text-zinc-500 text-center py-4">Loading…</p>
        ) : activities.length === 0 ? (
          <p className="text-xs text-zinc-500 text-center py-4">No activity yet</p>
        ) : (
          <ul className="space-y-2.5">
            {activities.slice(0, max).map((a) => {
              const Icon = ACTIVITY_ICONS[a.type] ?? Clock;
              const tone = ACTIVITY_TONES[a.type] ?? DEFAULT_TONE;
              const isAutomation = a.type.startsWith("automation.");
              const automationId = isAutomation ? (a.data?.automation_id as string | undefined) : undefined;
              const body = (
                <>
                  <div className={"mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full " + tone.bg}>
                    <Icon className={"h-3.5 w-3.5 " + tone.icon} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-200">{a.summary || a.type}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{formatRelative(a.occurred_at)}</p>
                  </div>
                </>
              );
              return (
                <li key={a.id}>
                  {automationId ? (
                    <Link to={`/automations/${automationId}`} className="flex items-start gap-3 rounded -m-1 p-1 hover:bg-zinc-800/40">
                      {body}
                    </Link>
                  ) : (
                    <div className="flex items-start gap-3">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ─── Tasks ────────────────────────────────────────────────────────────────
/**
 * Open tasks for a contact. Self-contained: New-task button opens an
 * embedded create dialog; clicking a task row opens it for editing
 * (same dialog in edit mode — no separate modal). The component is
 * intentionally identical wherever it renders — contact-detail page,
 * inbox info rail, anywhere else — so behaviour stays in lockstep.
 */
export function TasksPanel({
  contactId, max = 8,
}: {
  contactId: string;
  max?: number;
}) {
  const toast = useToast();
  const [tasks, setTasks] = useState<Task[] | null>(null);
  // null = closed, "new" = create form, Task = edit form. One shape both
  // for opening fresh and reopening to edit.
  const [editing, setEditing] = useState<Task | "new" | null>(null);

  const reload = () => {
    let cancelled = false;
    api.get<{ tasks: Task[] }>("/tasks", { contact_id: contactId, limit: max })
      .then((d) => { if (!cancelled) setTasks(d.tasks); })
      .catch(() => { if (!cancelled) setTasks([]); });
    return () => { cancelled = true; };
  };
  useEffect(() => reload(), [contactId, max]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (t: Task) => {
    if (!tasks) return;
    const next = t.status === "completed" ? "pending" : "completed";
    setTasks((prev) => prev ? prev.map((x) => x.id === t.id
      ? { ...x, status: next, completed_at: next === "completed" ? new Date().toISOString() : null }
      : x,
    ) : prev);
    try {
      await api.patch(`/tasks/${t.id}`, { status: next });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      setTasks((prev) => prev ? prev.map((x) => x.id === t.id ? t : x) : prev);
    }
  };

  return (
    <Card
      icon={CheckSquare}
      title="Tasks"
      action={<PlusButton onClick={() => setEditing("new")} aria="New task" />}
    >
      {tasks === null ? (
        <p className="text-xs text-zinc-500 px-4 py-3">Loading…</p>
      ) : tasks.length === 0 ? (
        <p className="text-xs text-zinc-500 px-4 py-3">No tasks</p>
      ) : (
        <ul className="divide-y divide-zinc-800/40">
          {tasks.slice(0, max).map((t) => (
            <li key={t.id} className="flex items-start gap-2 px-4 py-2">
              <input
                type="checkbox"
                checked={t.status === "completed"}
                onChange={() => toggle(t)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900 text-rose-500"
              />
              <button
                type="button"
                onClick={() => setEditing(t)}
                className="flex-1 min-w-0 text-left rounded -m-1 p-1 hover:bg-zinc-800/40"
              >
                <p className={"text-sm " + (t.status === "completed" ? "line-through text-zinc-500" : "text-zinc-200")}>{t.title}</p>
                {t.due_at && <p className="text-[10px] text-zinc-500 mt-0.5">{formatDateTime(t.due_at)}</p>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <TaskDialog
        target={editing}
        defaultContactId={contactId}
        onClose={() => setEditing(null)}
        onSaved={(saved, mode) => {
          setTasks((prev) => {
            if (!prev) return [saved];
            if (mode === "create") return [saved, ...prev];
            return prev.map((x) => x.id === saved.id ? saved : x);
          });
          setEditing(null);
        }}
        onDeleted={(deleted) => {
          setTasks((prev) => prev ? prev.filter((x) => x.id !== deleted.id) : prev);
          setEditing(null);
        }}
      />
    </Card>
  );
}

// ─── Appointments ─────────────────────────────────────────────────────────
export function AppointmentsPanel({ contactId, max = 5 }: { contactId: string; max?: number }) {
  const { data: appointments, error, reload } = usePanelData(
    () => api.get<{ appointments: Appointment[] }>("/appointments", { contact_id: contactId, limit: max }).then((d) => d.appointments),
    [contactId, max]
  );

  // Compact panel: hide entirely when there's no data and no error.
  // Surface a small error row if the fetch actually failed so the user
  // knows to retry instead of silently assuming "no appointments."
  if (error) {
    return (
      <Card icon={Calendar} title="Appointments">
        <PanelErrorRow message={error} onRetry={reload} />
      </Card>
    );
  }
  if (!appointments || appointments.length === 0) return null;
  return (
    <Card icon={Calendar} title="Appointments">
      <ul className="divide-y divide-zinc-800/40">
        {appointments.slice(0, max).map((a) => (
          <li key={a.id} className="px-4 py-2.5">
            <p className="text-sm text-zinc-200 truncate">{a.title}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{formatDateTime(a.starts_at)}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ─── Opportunities ────────────────────────────────────────────────────────
export function OpportunitiesPanel({ contactId, max = 5 }: { contactId: string; max?: number }) {
  const { data: opportunities, error, reload } = usePanelData(
    () => api.get<{ opportunities: Opportunity[] }>("/opportunities", { contact_id: contactId, limit: max }).then((d) => d.opportunities),
    [contactId, max]
  );

  if (error) {
    return (
      <Card icon={GitBranch} title="Opportunities">
        <PanelErrorRow message={error} onRetry={reload} />
      </Card>
    );
  }
  if (!opportunities || opportunities.length === 0) return null;
  return (
    <Card icon={GitBranch} title="Opportunities">
      <ul className="divide-y divide-zinc-800/40">
        {opportunities.slice(0, max).map((o) => (
          <li key={o.id} className="px-4 py-2.5">
            <Link to={`/pipelines?opportunity=${o.id}`} className="text-sm text-zinc-200 hover:text-rose-300 truncate block">{o.title}</Link>
            <p className="text-xs text-zinc-500 mt-0.5 capitalize">{o.stage_id} · ${((o.value_cents ?? 0) / 100).toLocaleString()}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ─── Payments ─────────────────────────────────────────────────────────────
/**
 * Payment activity for a contact: paid + open totals plus a list of
 * recent invoices. Self-contained: the New-invoice button opens an
 * embedded create dialog. Renders identically wherever it's used —
 * the contact-detail page and the inbox info rail share the exact
 * same component.
 */
export function PaymentsPanel({ contactId }: { contactId: string }) {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const reload = () => {
    let cancelled = false;
    // Per-contact panels show at most a handful of recent rows — cap the
    // fetch so a high-volume contact doesn't pull 100s of rows into a
    // sidebar that only renders the first 5–10.
    Promise.all([
      api.get<{ invoices: Invoice[] }>("/payments/invoices", { contact_id: contactId, limit: 20 }).catch(() => ({ invoices: [] })),
      api.get<{ payments: Payment[] }>("/payments", { contact_id: contactId, limit: 20 }).catch(() => ({ payments: [] })),
    ]).then(([inv, pay]) => {
      if (cancelled) return;
      setInvoices(inv.invoices);
      setPayments(pay.payments);
    });
    return () => { cancelled = true; };
  };
  useEffect(() => reload(), [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPaid = (payments ?? [])
    .filter((p) => p.status === "succeeded")
    .reduce((s, p) => s + p.amount_cents - p.refunded_amount_cents, 0);
  const openTotal = (invoices ?? [])
    .filter((i) => i.status === "open" || i.status === "uncollectible")
    .reduce((s, i) => s + (i.total_cents - i.amount_paid_cents), 0);

  return (
    <Card
      icon={CreditCard}
      title="Payments"
      action={<PlusButton onClick={() => setCreateOpen(true)} aria="New invoice" />}
    >
      <div className="grid grid-cols-2 gap-px border-b border-zinc-800/40 bg-zinc-800/40 text-center">
        <div className="bg-zinc-900/30 p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Total paid</p>
          <p className="text-sm font-semibold text-emerald-400 mt-0.5 tabular-nums">{formatMoney(totalPaid)}</p>
        </div>
        <div className="bg-zinc-900/30 p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Open</p>
          <p className={"text-sm font-semibold mt-0.5 tabular-nums " + (openTotal > 0 ? "text-amber-400" : "text-zinc-500")}>
            {formatMoney(openTotal)}
          </p>
        </div>
      </div>
      {(invoices === null || payments === null) ? (
        <p className="text-xs text-zinc-500 px-4 py-3">Loading…</p>
      ) : invoices.length === 0 && payments.length === 0 ? (
        <p className="text-xs text-zinc-500 px-4 py-3">No payments yet</p>
      ) : (
        <ul className="divide-y divide-zinc-800/40 max-h-72 overflow-y-auto">
          {invoices.slice(0, 5).map((inv) => (
            <li key={inv.id} className="flex items-center justify-between px-4 py-2.5 text-xs">
              <div className="min-w-0">
                <p className="text-zinc-200 truncate">Invoice {inv.number ?? inv.id.slice(0, 8)}</p>
                <p className="text-[10px] text-zinc-500">{formatRelative(inv.created_at)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge color={inv.status === "paid" ? "emerald" : inv.status === "open" ? "indigo" : "zinc"} size="xs">{inv.status}</Badge>
                <span className="font-medium text-zinc-200 tabular-nums">{formatMoney(inv.total_cents, inv.currency)}</span>
                {inv.hosted_url && (
                  <a href={inv.hosted_url} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-rose-300" aria-label="Open">
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <InvoiceCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        contactId={contactId}
        onCreated={() => { setCreateOpen(false); reload(); }}
      />
    </Card>
  );
}

function InvoiceCreateDialog({
  open, onClose, contactId, onCreated,
}: { open: boolean; onClose: () => void; contactId: string; onCreated: () => void }) {
  const toast = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [lineItems, setLineItems] = useState<{ name: string; quantity: number; unit_amount_cents: number; product_id?: string | null }[]>([
    { name: "", quantity: 1, unit_amount_cents: 0 },
  ]);
  const [currency, setCurrency] = useState("USD");
  const [sendNow, setSendNow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get<{ products: Product[] }>("/payments/products").then((d) => setProducts(d.products)).catch(() => {});
      setLineItems([{ name: "", quantity: 1, unit_amount_cents: 0 }]);
      setSendNow(false);
    }
  }, [open]);

  const total = lineItems.reduce((s, li) => s + li.quantity * li.unit_amount_cents, 0);
  const updateLine = (i: number, p: Partial<typeof lineItems[number]>) => {
    setLineItems((prev) => prev.map((li, idx) => idx === i ? { ...li, ...p } : li));
  };
  const addLine = () => setLineItems((prev) => [...prev, { name: "", quantity: 1, unit_amount_cents: 0 }]);
  const removeLine = (i: number) => setLineItems((prev) => prev.filter((_, idx) => idx !== i));
  const useProduct = (i: number, productId: string) => {
    if (!productId) return updateLine(i, { product_id: null });
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    updateLine(i, { product_id: p.id, name: p.name, unit_amount_cents: p.amount_cents });
    setCurrency(p.currency);
  };

  const submit = async () => {
    if (lineItems.some((l) => !l.name.trim() || l.unit_amount_cents <= 0)) {
      toast.error("Each line item needs a name and amount");
      return;
    }
    setSaving(true);
    try {
      await api.post("/payments/invoices", { contact_id: contactId, currency, line_items: lineItems, send_now: sendNow });
      toast.success(sendNow ? "Invoice sent" : "Draft saved");
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="New invoice" size="lg">
      <div className="space-y-3">
        <div>
          <Label>Line items</Label>
          <ul className="space-y-2">
            {lineItems.map((li, i) => (
              <li key={i} className="grid grid-cols-[1fr_5rem_6rem_auto] gap-2 items-center">
                <div className="space-y-1">
                  <Input value={li.name} onChange={(e) => updateLine(i, { name: e.target.value })} placeholder="Description" className="h-8" />
                  {products.length > 0 && (
                    <Select value={li.product_id ?? ""} onChange={(e) => useProduct(i, e.target.value)} className="h-7 text-xs">
                      <option value="">— Or pick a product —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {formatMoney(p.amount_cents, p.currency)}</option>)}
                    </Select>
                  )}
                </div>
                <Input type="number" min={1} value={li.quantity} onChange={(e) => updateLine(i, { quantity: parseInt(e.target.value, 10) || 1 })} className="h-8" />
                <Input type="number" step="0.01" value={li.unit_amount_cents / 100} onChange={(e) => updateLine(i, { unit_amount_cents: Math.round(parseFloat(e.target.value || "0") * 100) })} className="h-8" />
                <button
                  type="button"
                  onClick={() => removeLine(i)}
                  disabled={lineItems.length <= 1}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300 disabled:opacity-30"
                  aria-label="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={addLine} className="mt-2">
            Add line item
          </Button>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2">
          <span className="text-sm text-zinc-400">Total</span>
          <span className="text-base font-semibold text-zinc-100 tabular-nums">{formatMoney(total, currency)}</span>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <input
            type="checkbox"
            checked={sendNow}
            onChange={(e) => setSendNow(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-rose-500"
          />
          Send invoice now (publishes through your payment provider)
        </label>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={lineItems.length === 0 || total === 0}>
          {sendNow ? "Send invoice" : "Save as draft"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Automations ──────────────────────────────────────────────────────────
/**
 * Two sections per contact: active runs (currently running or parked at a
 * wait step) and a per-automation summary of completed runs. The summary
 * collapses repeat runs into one row with a "× N" badge so a contact that
 * looped through "Welcome Drip" three times shows once instead of cluttering
 * the panel with three near-duplicate rows.
 */
export function AutomationsPanel({ contactId }: { contactId: string }) {
  const [data, setData] = useState<ContactAutomations | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<ContactAutomations>(`/contacts/${contactId}/automations`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ active: [], completed: [] }); });
    return () => { cancelled = true; };
  }, [contactId]);

  const total = (data?.active.length ?? 0) + (data?.completed.length ?? 0);
  const fromPath = `/contacts/${contactId}`;

  return (
    <Card icon={Zap} title="Automations" count={total || undefined}>
      {data === null ? (
        <p className="px-4 py-3 text-xs text-zinc-500">Loading…</p>
      ) : total === 0 ? (
        <p className="px-4 py-3 text-xs text-zinc-500">No automations have run for this contact yet.</p>
      ) : (
        <div className="divide-y divide-zinc-800/40">
          {data.active.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Active</p>
              <ul>
                {data.active.map((a) => (
                  <li key={a.run_id}>
                    <Link
                      to={`/automations/${a.automation_id}`}
                      state={{ from: fromPath }}
                      className="flex items-start gap-3 px-4 py-2 hover:bg-zinc-800/40"
                    >
                      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                        {a.status === "waiting"
                          ? <Hourglass className="h-3.5 w-3.5 text-amber-300" />
                          : <Clock className="h-3.5 w-3.5 animate-pulse text-amber-300" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-zinc-200">{a.automation_name}</p>
                        <p className="mt-0.5 text-xs text-zinc-500">
                          {a.status === "waiting" && a.resume_at
                            ? <>Waiting{a.stage ? <> at <span className="font-mono">{a.stage}</span></> : null} · resumes {formatRelative(a.resume_at)}</>
                            : <>Running · started {formatRelative(a.started_at)}</>}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.completed.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Completed</p>
              <ul>
                {data.completed.map((c) => {
                  const tone = c.last_status === "succeeded" ? { bg: "bg-emerald-500/10", icon: "text-emerald-300", Icon: CheckCircle2 }
                    : c.last_status === "failed"             ? { bg: "bg-rose-500/10",    icon: "text-rose-300",    Icon: AlertCircle }
                    :                                          { bg: "bg-zinc-800",        icon: "text-zinc-400",    Icon: Clock };
                  const Icon = tone.Icon;
                  return (
                    <li key={c.automation_id}>
                      <Link
                        to={`/automations/${c.automation_id}`}
                        state={{ from: fromPath }}
                        className="flex items-start gap-3 px-4 py-2 hover:bg-zinc-800/40"
                      >
                        <div className={"mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full " + tone.bg}>
                          <Icon className={"h-3.5 w-3.5 " + tone.icon} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="min-w-0 truncate text-sm text-zinc-200">{c.automation_name}</p>
                            {c.run_count > 1 && (
                              <Badge color="zinc" size="xs">× {c.run_count}</Badge>
                            )}
                          </div>
                          <p className="mt-0.5 truncate text-xs text-zinc-500">
                            {c.last_status === "failed" && c.last_error
                              ? <span className="text-rose-400">{c.last_error}</span>
                              : <>last run {formatRelative(c.last_run_at)}</>}
                          </p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Do Not Disturb (per-channel consent) ─────────────────────────────────
/**
 * Per-channel opt-out controls. The master "Block all channels" toggle
 * sits on top — flipping it adds an `'all'` row to `contact_consent`
 * which short-circuits sends on every channel. The per-channel rows
 * underneath let the user block one channel without affecting others
 * (e.g. opt out of SMS but keep email allowed). Anything not listed
 * defaults to allowed.
 *
 * The runtime gate lives in `lib/messaging.ts::assertCanSend` — it
 * runs before every outbound message, so toggling here is the single
 * place that controls deliverability for a contact.
 */
const DND_CHANNELS: Array<{ key: string; label: string; icon: React.ComponentType<{ className?: string }>; has: (c: Contact) => boolean }> = [
  { key: "sms",       label: "SMS",         icon: MessageSquare, has: (c) => !!c.phone },
  { key: "email",     label: "Email",       icon: Mail,          has: (c) => !!c.email },
  { key: "voice",     label: "Voice",       icon: Phone,         has: (c) => !!c.phone },
  { key: "imessage",  label: "iMessage",    icon: MessageSquare, has: (c) => !!(c.phone || c.email) },
  { key: "whatsapp",  label: "WhatsApp",    icon: MessageSquare, has: (c) => !!(c.socials?.whatsapp || c.phone) },
  { key: "instagram", label: "Instagram",   icon: MessageSquare, has: (c) => !!c.socials?.instagram },
  { key: "facebook",  label: "Facebook",    icon: MessageSquare, has: (c) => !!c.socials?.facebook },
  { key: "linkedin",  label: "LinkedIn",    icon: MessageSquare, has: (c) => !!c.socials?.linkedin },
  { key: "twitter",   label: "Twitter / X", icon: MessageSquare, has: (c) => !!c.socials?.twitter },
  { key: "telegram",  label: "Telegram",    icon: MessageSquare, has: (c) => !!c.socials?.telegram },
  { key: "tiktok",    label: "TikTok",      icon: MessageSquare, has: (c) => !!c.socials?.tiktok },
];

export function ConsentPanel({ contact }: { contact: Contact }) {
  const contactId = contact.id;
  const toast = useToast();
  const [rows, setRows] = useState<ContactConsentRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = async () => {
    try {
      const d = await api.get<{ consent: ContactConsentRow[] }>(`/contacts/${contactId}/consent`);
      setRows(d.consent);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => { void reload(); }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (channel: string, current: ContactConsentRow | undefined) => {
    setBusy(channel);
    const nextStatus: "opted_in" | "opted_out" = current?.status === "opted_out" ? "opted_in" : "opted_out";
    try {
      await api.put(`/contacts/${contactId}/consent`, { channel, status: nextStatus, source: "user" });
      await reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setBusy(null);
    }
  };

  const lookup = new Map((rows ?? []).map((r) => [r.channel, r]));
  const masterRow = lookup.get("all");
  const masterBlocked = masterRow?.status === "opted_out";
  const blockedCount = (rows ?? []).filter((r) => r.status === "opted_out").length;
  // Hide channels the contact doesn't have an identity for — but keep
  // showing any channel that's already blocked so the user can see and
  // un-block it even if the underlying identity has been removed.
  const visibleChannels = DND_CHANNELS.filter((c) => c.has(contact) || lookup.get(c.key)?.status === "opted_out");

  return (
    <Card
      icon={masterBlocked ? ShieldAlert : ShieldCheck}
      title="Do not disturb"
      count={blockedCount || undefined}
    >
      {rows === null ? (
        <p className="px-4 py-3 text-xs text-zinc-500">Loading…</p>
      ) : (
        <div>
          {/* Master kill */}
          <button
            type="button"
            onClick={() => toggle("all", masterRow)}
            disabled={busy === "all"}
            className={cn(
              "flex w-full items-center justify-between gap-3 px-4 py-3 transition",
              masterBlocked ? "bg-rose-500/10 hover:bg-rose-500/20" : "hover:bg-zinc-800/40",
            )}
          >
            <div className="min-w-0 flex-1 text-left">
              <p className={cn("text-sm font-medium", masterBlocked ? "text-rose-200" : "text-zinc-200")}>
                Block all channels
              </p>
              <p className="mt-0.5 text-xs text-zinc-500">
                {masterBlocked
                  ? (masterRow?.reason ? `Blocked: ${masterRow.reason}` : "Blocked — no outbound will deliver")
                  : "Master kill switch — overrides every channel"}
              </p>
            </div>
            <ToggleDot active={masterBlocked} />
          </button>

          <div className="border-t border-zinc-800/40" />

          {/* Per-channel */}
          <ul className="divide-y divide-zinc-800/40">
            {visibleChannels.map((c) => {
              const row = lookup.get(c.key);
              const blocked = row?.status === "opted_out";
              const Icon = c.icon;
              const disabled = masterBlocked || busy === c.key;
              return (
                <li key={c.key}>
                  <button
                    type="button"
                    onClick={() => !disabled && toggle(c.key, row)}
                    disabled={disabled}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2 text-left transition",
                      disabled && "opacity-50",
                      !disabled && (blocked ? "bg-rose-500/5 hover:bg-rose-500/10" : "hover:bg-zinc-800/40"),
                    )}
                  >
                    <Icon className={cn("h-3.5 w-3.5 shrink-0", blocked ? "text-rose-400" : "text-zinc-500")} />
                    <div className="min-w-0 flex-1">
                      <p className={cn("text-sm", blocked ? "text-rose-200" : "text-zinc-200")}>{c.label}</p>
                      {row?.reason && (
                        <p className="mt-0.5 truncate text-[10px] text-zinc-500">{row.reason}</p>
                      )}
                    </div>
                    <ToggleDot active={blocked} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

function ToggleDot({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
        active ? "bg-rose-500/40" : "bg-zinc-700",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 rounded-full bg-zinc-100 transition-transform",
          active ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </span>
  );
}

// ─── Notes ────────────────────────────────────────────────────────────────
const NOTES_PAGE_SIZE = 25;

export function NotesPanel({ contactId }: { contactId: string }) {
  const toast = useToast();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [total, setTotal] = useState(0);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setNotes(null);
    api.get<{ notes: Note[]; total?: number }>("/notes", { contact_id: contactId, limit: NOTES_PAGE_SIZE })
      .then((d) => { if (!cancelled) { setNotes(d.notes); setTotal(d.total ?? d.notes.length); } })
      .catch(() => { if (!cancelled) { setNotes([]); setTotal(0); } });
    return () => { cancelled = true; };
  }, [contactId]);

  const loadMore = async () => {
    if (!notes || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await api.get<{ notes: Note[]; total?: number }>("/notes", {
        contact_id: contactId, limit: NOTES_PAGE_SIZE, offset: notes.length,
      });
      // Filter out any optimistic-temp IDs we might have re-fetched against —
      // the backend doesn't return tmp_* ids so this is a no-op in practice
      // but keeps types stable if the contract ever shifts.
      setNotes((prev) => prev ? [...prev, ...d.notes] : d.notes);
      if (typeof d.total === "number") setTotal(d.total);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load more notes");
    } finally {
      setLoadingMore(false);
    }
  };

  const submit = async () => {
    if (!body.trim()) return;
    setSaving(true);
    const optimistic: Note = {
      id: `tmp_${Date.now()}`,
      contact_id: contactId,
      opportunity_id: null,
      body: body.trim(),
      pinned: false,
      author_id: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setNotes((prev) => prev ? [optimistic, ...prev] : [optimistic]);
    setTotal((t) => t + 1);
    const sent = body;
    setBody("");
    try {
      const res = await api.post<{ note: Note }>("/notes", { contact_id: contactId, body: sent });
      setNotes((prev) => prev ? prev.map((n) => n.id === optimistic.id ? res.note : n) : prev);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save note");
      setNotes((prev) => prev ? prev.filter((n) => n.id !== optimistic.id) : prev);
      setTotal((t) => Math.max(0, t - 1));
      setBody(sent);
    } finally {
      setSaving(false);
    }
  };

  const hasMore = !!notes && notes.length < total;

  return (
    <Card icon={StickyNote} title="Notes">
      <div className="p-3 space-y-2">
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note…" className="min-h-12 text-xs" />
        <Button size="sm" onClick={submit} disabled={!body.trim() || saving} className="w-full">
          Save note
        </Button>
      </div>
      {notes && notes.length > 0 && (
        <>
          <ul className="divide-y divide-zinc-800/40 max-h-72 overflow-y-auto">
            {notes.map((n) => (
              <li key={n.id} className="px-4 py-2.5">
                <p className="text-xs text-zinc-200 whitespace-pre-wrap">{n.body}</p>
                <p className="text-[10px] text-zinc-500 mt-1">{formatRelative(n.created_at)}</p>
              </li>
            ))}
          </ul>
          {hasMore && (
            <div className="border-t border-zinc-800/40 px-3 py-2">
              <Button size="sm" variant="outline" onClick={loadMore} disabled={loadingMore} className="w-full">
                {loadingMore ? "Loading…" : `Load more (${total - notes.length} remaining)`}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ─── Tags ─────────────────────────────────────────────────────────────────
/**
 * Tag picker. Renders the contact's current tags + a typeable suggestion
 * box that shows each existing tag in its color (single click adds it).
 * Pressing Enter on a non-matching string creates a brand-new tag.
 *
 * `contact` is passed in (rather than a contact id) because we need the
 * already-resolved tag list on the contact to render the chips. After
 * any mutation the caller is notified via `onContactChange` so the
 * contact-detail header / inbox info panel can stay in sync.
 */
export function TagsPanel({
  contact, onContactChange,
}: {
  contact: Contact;
  onContactChange?: (next: Contact) => void;
}) {
  const toast = useToast();
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ tags: Tag[] }>("/tags")
      .then((d) => { if (!cancelled) setAllTags(d.tags); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [contact.id]);

  // Click-outside / Esc closes the picker.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const addTag = async (name: string) => {
    if (!name.trim()) return;
    const trimmed = name.trim();
    if (contact.tags.some((t) => t.name.toLowerCase() === trimmed.toLowerCase())) {
      setInput(""); setOpen(false); return;
    }
    const optimistic = { id: `tmp_${Date.now()}`, name: trimmed, color: "indigo" };
    onContactChange?.({ ...contact, tags: [...contact.tags, optimistic] });
    setInput(""); setOpen(false);
    try {
      await api.post(`/contacts/${contact.id}/tags`, { tag: trimmed });
      // Re-fetch the contact to pick up the real tag id + color.
      const r = await api.get<{ contact: Contact }>(`/contacts/${contact.id}`);
      onContactChange?.(r.contact);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      onContactChange?.(contact);
    }
  };
  const removeTag = async (name: string) => {
    const before = contact.tags;
    onContactChange?.({ ...contact, tags: contact.tags.filter((t) => t.name !== name) });
    try {
      await api.delete(`/contacts/${contact.id}/tags/${encodeURIComponent(name)}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      onContactChange?.({ ...contact, tags: before });
    }
  };

  const attached = new Set(contact.tags.map((t) => t.name.toLowerCase()));
  const needle = input.trim().toLowerCase();
  const suggestions = allTags
    .filter((t) => !attached.has(t.name.toLowerCase()))
    .filter((t) => !needle || t.name.toLowerCase().includes(needle))
    .slice(0, 12);
  const exactMatch = needle && allTags.some((t) => t.name.toLowerCase() === needle);

  return (
    <Card icon={TagIcon} title="Tags">
      <div className="p-3 space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {contact.tags.length === 0 && <span className="text-xs text-zinc-600">No tags yet</span>}
          {contact.tags.map((t) => (
            <Badge key={t.id} color={t.color} size="sm" onRemove={() => removeTag(t.name)}>{t.name}</Badge>
          ))}
        </div>
        <div ref={wrapRef} className="relative">
          <Input
            value={input}
            onChange={(e) => { setInput(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && input.trim()) {
                e.preventDefault();
                void addTag(input.trim());
              }
            }}
            placeholder="Add a tag…"
            leftIcon={<TagIcon className="h-3.5 w-3.5" />}
            className="h-8 text-xs"
          />
          {open && (suggestions.length > 0 || (needle && !exactMatch)) && (
            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl shadow-shade/40 animate-fade-in">
              {suggestions.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => void addTag(t.name)}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-zinc-900"
                >
                  <Badge color={t.color} size="xs">{t.name}</Badge>
                  {t.usage_count != null && (
                    <span className="ml-auto text-[10px] text-zinc-600">{t.usage_count}</span>
                  )}
                </button>
              ))}
              {needle && !exactMatch && (
                <button
                  type="button"
                  onClick={() => void addTag(input.trim())}
                  className="flex w-full items-center gap-2 border-t border-zinc-800/60 px-2 py-1.5 text-left text-xs text-zinc-300 hover:bg-zinc-900"
                >
                  <Plus className="h-3 w-3 text-zinc-500" />
                  Create new tag <span className="font-medium text-zinc-100">"{input.trim()}"</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Layout primitives ────────────────────────────────────────────────────
/**
 * Standard panel chrome — header with icon + title + optional count or
 * action button, then a body slot. Kept minimal so the panels above stay
 * focused on data shapes, not chrome.
 */
function Card({
  icon: Icon, title, count, action, children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      <div className="flex items-center justify-between border-b border-zinc-800/70 px-4 py-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
          <Icon className="h-3.5 w-3.5" /> {title}
        </h3>
        {action ?? (count != null && <span className="text-[10px] text-zinc-500">{count}</span>)}
      </div>
      {children}
    </div>
  );
}

function PlusButton({ onClick, aria }: { onClick: () => void; aria: string }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
      aria-label={aria}
    >
      <Plus className="h-3.5 w-3.5" />
    </button>
  );
}

// Avoid unused-import warning when consumers want to render the trash icon
// alongside their own panels — re-export so they don't double-import.
export { Trash2 };
